import React, {forwardRef} from 'react';
import {ErrorMessage, Field, FieldArray, Form, Formik, FormikHelpers,} from 'formik';
import * as Yup from 'yup';
import {Button, Container, Form as BootstrapForm, InputGroup,} from 'react-bootstrap';
import {JSONSchema, JSONSchemaProperty} from "../json-schema";

interface FormGeneratorProps {
    schema: JSONSchema;
    defaults: any;
    onSubmit: (values: any, formikHelpers: FormikHelpers<any>) => void | Promise<void>;
}

const buildInitialValues = (
    schema: JSONSchemaProperty,
    defaults: any = {}
): any => {
    if (schema.type === 'object' && 'properties' in schema) {
        const result: any = {};
        for (const key of Object.keys(schema.properties)) {
            result[key] = buildInitialValues(schema.properties[key], defaults?.[key]);
        }
        return result;
    }

    if (schema.type === 'array') {
        return defaults ?? [];
    }

    if (defaults !== undefined) return defaults;

    switch (schema.type) {
        case 'string':
            return '';
        case 'number':
            return 0;
        case 'boolean':
            return false;
    }
};

const buildValidationSchema = (schema: JSONSchemaProperty): Yup.AnySchema => {
    if (schema.type === 'object' && 'properties' in schema) {
        const shape: Record<string, Yup.AnySchema> = {};
        for (const key of Object.keys(schema.properties)) {
            let fieldSchema = buildValidationSchema(schema.properties[key]);
            if (schema.required?.includes(key)) {
                fieldSchema = fieldSchema.required('Wymagane');
            }
            shape[key] = fieldSchema;
        }
        return Yup.object().shape(shape);
    }

    if (schema.type === 'array') {
        let itemSchema = buildValidationSchema(schema.items);
        let arr = Yup.array().of(itemSchema);
        if (schema.minItems) arr = arr.min(schema.minItems);
        if (schema.maxItems) arr = arr.max(schema.maxItems);
        return arr;
    }

    switch (schema.type) {
        case 'string': {
            let s = Yup.string();
            if ('pattern' in schema && schema.pattern) {
                s = s.matches(new RegExp(schema.pattern), 'Niepoprawny format');
            }
            return s;
        }
        case 'number':
            return Yup.number();
        case 'boolean':
            return Yup.boolean();
        default:
            return Yup.mixed();
    }
};

const renderFields = (
    schema: JSONSchema,
    path: string = '',
    required: string[] = []
): React.ReactNode[] => {
    const fields: React.ReactNode[] = [];

    for (const [key, propSchema] of Object.entries(schema.properties)) {
        const fullPath = path ? `${path}.${key}` : key;
        const label = propSchema.title || key;
        const isRequired = required.includes(key);

        if (propSchema.type === 'object' && 'properties' in propSchema) {
            fields.push(
                <fieldset key={fullPath} className="border p-3 mb-3 rounded">
                    <legend className="h6">{label}</legend>
                    {renderFields(propSchema, fullPath, propSchema.required || [])}
                </fieldset>
            );
        } else if (propSchema.type === 'array') {
            const itemSchema = propSchema.items;
            const showAsSelect = 'enum' in itemSchema && Array.isArray(itemSchema.enum);
            const itemType = (itemSchema as any).type ?? 'string';

            fields.push(
                <div key={fullPath} className="mb-3">
                    <BootstrapForm.Label>{label}</BootstrapForm.Label>
                    <FieldArray name={fullPath}>
                        {({push, remove, form}) => (
                            <>
                                {(form.values as any)[fullPath]?.map((_: any, index: number) => {
                                    const itemPath = `${fullPath}[${index}]`;
                                    return (
                                        <InputGroup className="mb-2" key={itemPath}>
                                            {showAsSelect ? (
                                                <Field as="select" name={itemPath} className="form-select">
                                                    <option value="">-- wybierz --</option>
                                                    {itemSchema.enum!.map((val: any) => (
                                                        <option key={val} value={val}>
                                                            {val}
                                                        </option>
                                                    ))}
                                                </Field>
                                            ) : (
                                                <Field name={itemPath} type={itemType} className="form-control"/>
                                            )}
                                            <Button
                                                variant="outline-danger"
                                                onClick={() => remove(index)}
                                            >
                                                −
                                            </Button>
                                        </InputGroup>
                                    );
                                })}
                                <Button
                                    variant="outline-primary"
                                    onClick={() => push(itemType === 'number' ? 0 : '')}
                                >
                                    + Dodaj
                                </Button>
                            </>
                        )}
                    </FieldArray>
                    <ErrorMessage name={fullPath} component="div" className="text-danger"/>
                </div>
            );
        } else {
            const showAsSelect = 'enum' in propSchema && Array.isArray(propSchema.enum);

            fields.push(
                <BootstrapForm.Group key={fullPath} className="mb-3">
                    {propSchema.type !== 'boolean' && (
                        <BootstrapForm.Label>{label}</BootstrapForm.Label>
                    )}

                    {showAsSelect ? (
                        <Field as="select" name={fullPath} className="form-select">
                            <option value="">-- wybierz --</option>
                            {propSchema.enum!.map((val: any) => (
                                <option key={val} value={val}>
                                    {val}
                                </option>
                            ))}
                        </Field>
                    ) : propSchema.type === 'boolean' ? (
                        <div className="form-check">
                            <Field
                                type="checkbox"
                                name={fullPath}
                                className="form-check-input"
                                id={fullPath}
                            />
                            <label className="form-check-label" htmlFor={fullPath}>
                                {label}
                            </label>
                        </div>
                    ) : (
                        <Field
                            type={propSchema.type}
                            name={fullPath}
                            className="form-control"
                            required={isRequired}
                        />
                    )}
                    <ErrorMessage name={fullPath} component="div" className="text-danger"/>
                </BootstrapForm.Group>
            );
        }
    }

    return fields;
};

export const FormGenerator = forwardRef<any, FormGeneratorProps>(
    ({schema, defaults, onSubmit}, ref) => {
        const initialValues = buildInitialValues(schema, defaults);
        const validationSchema = buildValidationSchema(schema);

        return (
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                innerRef={ref}
            >
                {() => (
                    <Form>
                        <Container>{renderFields(schema, '', [...(schema.required ?? [])] as string[])}</Container>
                    </Form>
                )}
            </Formik>
        );
    }
);